import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const EditorialesPage = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [editoriales, setEditoriales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para filtros
  const [buscar, setBuscar] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('A');
  
  // Estados para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const elementosPorPagina = 10;
  
  // Estados para el modal
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [editorialActual, setEditorialActual] = useState(() => ({ id: null, descripcion: '', estado: 'A' }));

  useEffect(() => {
    setPaginaActual(1); // Reset a la primera página cuando cambia el filtro
  }, [estadoFiltro, buscar]);

  useEffect(() => {
    cargarEditoriales();
  }, [estadoFiltro, paginaActual, buscar]);

  const cargarEditoriales = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        estado: estadoFiltro,
        skip: (paginaActual - 1) * elementosPorPagina,
        limit: elementosPorPagina
      };
      
      if (buscar.trim()) {
        params.buscar = buscar.trim();
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/editoriales`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setEditoriales(response.data);
      
      // Obtener el conteo total del header X-Total-Count
      const totalCount = parseInt(response.headers['x-total-count'] || '0');
      setTotalPaginas(Math.ceil(totalCount / elementosPorPagina));
    } catch (err) {
      console.error('Error al cargar editoriales:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg).join(', '));
      } else {
        setError(errorDetail || t('publishers.errorLoading'));
      }
    } finally {
      setLoading(false);
    }
  }, [token, estadoFiltro, paginaActual, buscar]);

  const handleBuscar = (e) => {
    e.preventDefault();
    setPaginaActual(1);
    cargarEditoriales();
  };

  const abrirModalCrear = () => {
    setModoEdicion(false);
    setEditorialActual({ id: null, descripcion: '', estado: 'A' });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const abrirModalEditar = (editorial) => {
    setModoEdicion(true);
    setEditorialActual({ ...editorial });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const cerrarModal = () => {
    setShowModal(false);
    setEditorialActual({ id: null, descripcion: '', estado: 'A' });
    setError('');
    setSuccess('');
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (modoEdicion) {
        // Actualizar
        await axios.put(
          `${API_BASE_URL}/api/editoriales/${editorialActual.id}`,
          {
            descripcion: editorialActual.descripcion,
            estado: editorialActual.estado
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess(t('publishers.updatedSuccess'));
      } else {
        // Crear
        await axios.post(
          `${API_BASE_URL}/api/editoriales`,
          { descripcion: editorialActual.descripcion },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess(t('publishers.createdSuccess'));
      }
      
      await cargarEditoriales();
      cerrarModal();
    } catch (err) {
      console.error('Error al guardar editorial:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg).join(', '));
      } else {
        setError(errorDetail || t('publishers.errorSaving'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm(t('publishers.deleteConfirm'))) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.delete(`${API_BASE_URL}/api/editoriales/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(t('publishers.deletedSuccess'));
      await cargarEditoriales();
    } catch (err) {
      console.error('Error al eliminar editorial:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg).join(', '));
      } else {
        setError(errorDetail || t('publishers.errorDeleting'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h2 className="mb-0">
            <i className="bi bi-building me-2"></i>
            {t('publishers.title')}
          </h2>
          <p className="text-muted">{t('publishers.subtitle')}</p>
        </div>
      </div>

      {/* Alertas */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle-fill me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Barra de herramientas */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-5">
              <form onSubmit={handleBuscar}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('publishers.searchPlaceholder')}
                    value={buscar}
                    onChange={(e) => setBuscar(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" type="submit">
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>
            </div>
            
            <div className="col-md-3">
              <select
                className="form-select"
                value={estadoFiltro}
                onChange={(e) => setEstadoFiltro(e.target.value)}
              >
                <option value="A">{t('publishers.active')}</option>
                <option value="I">{t('publishers.inactive')}</option>
              </select>
            </div>
            
            <div className="col-md-4 text-end">
              <button
                className="btn btn-primary"
                onClick={abrirModalCrear}
              >
                <i className="bi bi-plus-circle me-2"></i>
                {t('publishers.newPublisher')}
              </button>
            </div>
          </div>
        </div>
      </div>

    {/* Tabla de editoriales */}
        <div className="card">
          <div className="card-body">
            {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('publishers.loading')}</span>
              </div>
            </div>
            ) : editoriales.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox display-4 d-block mb-3"></i>
              <p>{t('publishers.noResults')}</p>
            </div>
            ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                <tr>
                  <th style={{ width: '10%' }}>#</th>
                  <th style={{ width: '60%' }}>{t('publishers.description')}</th>
                  <th style={{ width: '15%' }}>{t('publishers.status')}</th>
                  <th style={{ width: '15%' }} className="text-end">{t('publishers.actions')}</th>
                </tr>
                </thead>
                <tbody>
                {editoriales.map((editorial, index) => {
                  const virtualId = (paginaActual - 1) * elementosPorPagina + index + 1;
                  return (
                    <tr key={editorial.id}>
                    <td>{virtualId}</td>
                    <td>{editorial.descripcion}</td>
                    <td>
                      {editorial.estado === 'A' ? (
                        <span className="badge bg-success">{t('publishers.active')}</span>
                      ) : (
                        <span className="badge bg-secondary">{t('publishers.inactive')}</span>
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => abrirModalEditar(editorial)}
                        title={t('publishers.edit')}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      {editorial.estado === 'A' && (
                        <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleEliminar(editorial.id)}
                        title={t('publishers.delete')}
                        >
                        <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
            )}
            
            <div className="mt-3 d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {t('publishers.showing')} {editoriales.length > 0 ? ((paginaActual - 1) * elementosPorPagina + 1) : 0} - {Math.min(paginaActual * elementosPorPagina, (paginaActual - 1) * elementosPorPagina + editoriales.length)} {t('publishers.records')}
            </small>
            
            {totalPaginas > 1 && (
              <nav>
                <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setPaginaActual(paginaActual - 1)}
                    disabled={paginaActual === 1}
                  >
                    {t('publishers.previous')}
                  </button>
                </li>
                
                {[...Array(totalPaginas)].map((_, index) => {
                    const pageNum = index + 1;
                    // Mostrar solo 5 páginas alrededor de la actual
                    if (
                      pageNum === 1 || 
                      pageNum === totalPaginas || 
                      (pageNum >= paginaActual - 2 && pageNum <= paginaActual + 2)
                    ) {
                      return (
                        <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setPaginaActual(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    } else if (
                      pageNum === paginaActual - 3 || 
                      pageNum === paginaActual + 3
                    ) {
                      return <li key={pageNum} className="page-item disabled"><span className="page-link">...</span></li>;
                    }
                    return null;
                  })}
                  
                  <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginaActual(paginaActual + 1)}
                      disabled={paginaActual === totalPaginas}
                    >
                      {t('publishers.next')}
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>

      {/* Modal para crear/editar */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modoEdicion ? t('publishers.editPublisher') : t('publishers.newPublisher')}
                </h5>
                <button type="button" className="btn-close" onClick={cerrarModal}></button>
              </div>
              
              <form onSubmit={handleGuardar}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="descripcion" className="form-label">
                      {t('publishers.publisherName')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="descripcion"
                      value={editorialActual.descripcion}
                      onChange={(e) => setEditorialActual({ ...editorialActual, descripcion: e.target.value })}
                      required
                      maxLength={255}
                      autoFocus
                    />
                  </div>
                  
                  {modoEdicion && (
                    <div className="mb-3">
                      <label htmlFor="estado" className="form-label">
                        {t('publishers.status')}
                      </label>
                      <select
                        className="form-select"
                        id="estado"
                        value={editorialActual.estado}
                        onChange={(e) => setEditorialActual({ ...editorialActual, estado: e.target.value })}
                      >
                        <option value="A">{t('publishers.active')}</option>
                        <option value="I">{t('publishers.inactive')}</option>
                      </select>
                    </div>
                  )}
                </div>
                
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                    {t('publishers.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {t('publishers.saving')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        {t('publishers.save')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorialesPage;
